import { gql } from "@apollo/client"
import { useEffect, useMemo, useState } from "react"
import { Col, Collapse, Container, Row } from "react-bootstrap"
import { useApolloClient } from "@apollo/client/react"
import { useSearchParams } from "react-router-dom"
import { useModifyPage, useModifyRecordsPerPage, usePaginationData, useSetSearchUrlParams } from "../../../../hooks/usePagination"
import styles from "./SystemParameters.module.css"
import Parameter, { SystemParameterType } from "./parameter/Parameter"
import SearchContainer from "./search-container/SystemParametersSearchContainer"
import Pagination from "../../../../components/pagination/Pagination"
import Notification, { AlertItem } from "../../../../components/notification/Notification"
import LoadingSkeleton from "../../../../components/loading-skeleton/LoadingSkeleton"
import ErrorMessage from "../../../../components/error-message/ErrorMessage"

const SYSTEM_PARAMETERS = gql`
    query SystemParameters($searchParameters: SystemSearchParameters!, $orderBy: SystemParametersSortField, $direction: SortDirection, $pagination: PaginationInput!) {
        systemParameters(searchParameters: $searchParameters, orderBy: $orderBy, direction: $direction, pagination: $pagination) {
            maxPages
            currentPage
            recordsPerPage
            systemParameters {
                id
                name
                value
                description
                type
            }
        }
    }
`

interface SystemParametersQueryResult {
    systemParameters: {
        maxPages: number
        currentPage: number
        recordsPerPage: number
        systemParameters: SystemParameterType[]
    }
}

enum SYSTEM_PARAMETERS_SORT_DIRECTION {
    ASC = "asc",
    DESC = "desc"
}

enum SYSTEM_PARAMETERS_SORT_FIELD {
    ID = "id",
    NAME = "name",
    VALUE = "value"
}

export type SystemSearchParams = {
    id: number | null
    name: string | null
    value: string | null
}

export type SortSearchParamsType = {
    field: SYSTEM_PARAMETERS_SORT_FIELD | null
    direction: SYSTEM_PARAMETERS_SORT_DIRECTION | null
}

export default function SystemParameters() {
    const client = useApolloClient()
    const [ searchParams, setSearchParams ] = useSearchParams()
    const [ systemParameters, setSystemParameters ] = useState<SystemParameterType[]>([])
    const [ maxPages, setMaxPages ] = useState(-1)
    const [ loading, setLoading ] = useState(true)
    const [ alerts, setAlerts ] = useState<AlertItem[]>([])
    const [editingId, setEditingId] = useState<number | null>(null)
    const pagination = usePaginationData(searchParams, maxPages)
    const modifyPage = useModifyPage(setSearchParams, pagination)
    const modifyRecordsPerPage = useModifyRecordsPerPage(setSearchParams)
    const setSearchUrlParams = useSetSearchUrlParams(setSearchParams)
    const [ showError, setShowError ] = useState(false)
    const [ errorMessage, setErrorMessage ] = useState("")

    const searchParameters = useMemo<SystemSearchParams>(() => {
        let id: number | null = searchParams.get('id') !== null ? Number(searchParams.get('id')) : null
        if(isNaN(id as number)) {
            id = null
        }

        let name: string | null = searchParams.get('name')
        let value: string | null = searchParams.get('value')
        
        const ret: SystemSearchParams = {
            id,
            name,
            value
        }
        return ret
    }, [ searchParams ])

    const sortBy = useMemo<SortSearchParamsType>(() => {
        let orderBy = searchParams.get('orderby') as (SYSTEM_PARAMETERS_SORT_FIELD | null)
        let direction = searchParams.get('direction') as (SYSTEM_PARAMETERS_SORT_DIRECTION | null)

        if(orderBy !== null) {
            orderBy = orderBy.toLowerCase() as SYSTEM_PARAMETERS_SORT_FIELD
            if(!Object.values(SYSTEM_PARAMETERS_SORT_FIELD).includes(orderBy)) {
                orderBy = null
            }
        }

        if(orderBy === null) {
            direction = null
        }
        else {
            if(direction !== null) {
                direction = direction.toLowerCase() as SYSTEM_PARAMETERS_SORT_DIRECTION
                if(!Object.values(SYSTEM_PARAMETERS_SORT_DIRECTION).includes(direction)) {
                    direction = null
                }
            }
        }

        return {
            field: orderBy,
            direction
        }
    }, [ searchParams ])

    useEffect(() => {
        const getSystemParameters = async () => {
            setLoading(true)

            const data = await client.query<SystemParametersQueryResult>({
                query: SYSTEM_PARAMETERS,
                variables: {
                    searchParameters,
                    orderBy: sortBy.field ? sortBy.field.toUpperCase() : undefined,
                    direction: sortBy.direction ? sortBy.direction.toUpperCase() : undefined,
                    pagination
                },
                fetchPolicy: 'no-cache'
            })

            const { maxPages, recordsPerPage, currentPage, systemParameters} = data.data!.systemParameters
            setMaxPages(maxPages)

            if(pagination.recordsPerPage !== recordsPerPage) {
                modifyRecordsPerPage(recordsPerPage)
            }

            if(pagination.currentPage !== currentPage) {
                modifyPage(currentPage)
            }

            if(systemParameters && systemParameters.length > 0) {
                setShowError(false)
                setSystemParameters(systemParameters)
            }
            else {
                setShowError(true)
                setErrorMessage("Nincs találat")
            }

            setLoading(false)
        }
        getSystemParameters()
    }, [ client, sortBy, pagination, modifyPage, modifyRecordsPerPage, searchParameters ])

    const toggleSort = (field: SYSTEM_PARAMETERS_SORT_FIELD) => {
        if(loading) {
            return
        }

        let newDirection = SYSTEM_PARAMETERS_SORT_DIRECTION.ASC
        if(sortBy.field === field) {
            newDirection = sortBy.direction === SYSTEM_PARAMETERS_SORT_DIRECTION.ASC ? SYSTEM_PARAMETERS_SORT_DIRECTION.DESC : SYSTEM_PARAMETERS_SORT_DIRECTION.ASC
        }

        setSearchUrlParams({
            orderby: field,
            direction: newDirection
        })

        setLoading(true)
    }

    const getArrowSrc = (field: SYSTEM_PARAMETERS_SORT_FIELD) => {
        if (sortBy.field !== field) {
            return "/updown-arrow.png"
        }
        return sortBy.direction === "asc" ? "/uparrow.png" : "/downarrow.png"
    }

    const setSystemParameter = (systemParameter: SystemParameterType) => {
        setSystemParameters(prev => prev.map(x => x.id === systemParameter.id ? systemParameter : x))
    }
    
    return(
        <>
            <SearchContainer
                searchParameters={ searchParameters }
                setSearchParams={ setSearchParams }
                pagination= {
                    <Pagination
                        maxPages={ maxPages }
                        currentPage={ pagination.currentPage }
                        recordsPerPageProps="Rendszer paraméter / Oldal"
                        recordsPerPage={ pagination.recordsPerPage }
                        modifyPage={page => {
                            modifyPage(page)
                            setLoading(true)
                        }}
                        modifyRecordsPerPage={rpp => {
                            modifyRecordsPerPage(rpp)
                            setLoading(true)
                        }}
                    />
                }
            />

            <Container className={ styles.userContainer }>
                <Row className={styles.containerHead}>
                    <Col sm="3">
                        ID
                        <button onClick={() => toggleSort(SYSTEM_PARAMETERS_SORT_FIELD.ID)} className={styles.colButtons}>
                            <img id='id' className={styles.colButtonsImage} src={getArrowSrc(SYSTEM_PARAMETERS_SORT_FIELD.ID)} alt="sorting" />
                        </button>
                    </Col>

                    <Col sm="3">
                        Név
                        <button onClick={() => toggleSort(SYSTEM_PARAMETERS_SORT_FIELD.NAME)} className={styles.colButtons}>
                            <img id="name" className={styles.colButtonsImage} src={getArrowSrc(SYSTEM_PARAMETERS_SORT_FIELD.NAME)} alt="sorting" />
                        </button>
                    </Col>

                    <Col sm="4">
                        Érték
                        <button onClick={() => toggleSort(SYSTEM_PARAMETERS_SORT_FIELD.VALUE)} className={styles.colButtons}>
                            <img id="value" className={styles.colButtonsImage} src={getArrowSrc(SYSTEM_PARAMETERS_SORT_FIELD.VALUE)} alt="sorting" />
                        </button>
                    </Col>
                </Row>
                {
                    loading
                    ?
                        <LoadingSkeleton rows={pagination.recordsPerPage} cells={4} />
                    :
                        showError
                        ? 
                            <ErrorMessage message={errorMessage} />
                        :
                        systemParameters.map((param, index) =>
                            <Parameter
                                key={`system-param-id-${param.id}}`}
                                parameter={ param }
                                isEditing={editingId === param.id}
                                onStartEdit={() => setEditingId(param.id)}
                                onStopEdit={() => setEditingId(null)}
                                setAlerts={setAlerts}
                                odd={ index % 2 === 1 }
                                setSystemParameter={ setSystemParameter }
                            />
                        )
                }
            </Container>

            <Notification
                position="bottom-right"
                alerts={ alerts }
                dismiss={ setAlerts }
                as={ Collapse }
            />
        </>
    )
}